import React from 'react';
import { Search } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import type { Role } from '../../../services/memberService';
import { useMemberLabels } from './memberLabels';

interface MemberFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  roleFilter: string;
  onRoleChange: (value: string) => void;
  statusFilter: string;
  onStatusChange: (value: string) => void;
  assignableRoles: Role[];
}

/** Search box + role/status dropdowns for filtering the member list. */
export const MemberFilters: React.FC<MemberFiltersProps> = ({
  searchTerm,
  onSearchChange,
  roleFilter,
  onRoleChange,
  statusFilter,
  onStatusChange,
  assignableRoles,
}) => {
  const { t } = useLanguage();
  const { roleLabel } = useMemberLabels(assignableRoles);

  return (
    <div
      data-test-id="member-filters"
      className="flex flex-col sm:flex-row items-end gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-200"
    >
      <div className="relative flex-1 w-full">
        <label htmlFor="member-search" className="block text-sm font-medium text-gray-700 mb-1">
          {t('settingsMember.filters.search')}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="member-search"
            data-test-id="member-search-input"
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            placeholder={t('settingsMember.filters.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
      <div className="w-full sm:w-48">
        <label htmlFor="role-filter" className="block text-sm font-medium text-gray-700 mb-1">
          {t('settingsMember.filters.allRoles')}
        </label>
        <select
          id="role-filter"
          data-test-id="member-role-filter"
          value={roleFilter}
          onChange={(e) => onRoleChange(e.target.value)}
          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md border"
        >
          <option value="all">{t('settingsMember.filters.allRoles')}</option>
          {assignableRoles.map((role) => (
            <option key={role.name} value={role.name}>
              {role.display_name || roleLabel(role.name)}
            </option>
          ))}
        </select>
      </div>
      <div className="w-full sm:w-48">
        <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
          {t('settingsMember.filters.allStatuses')}
        </label>
        <select
          id="status-filter"
          data-test-id="member-status-filter"
          value={statusFilter}
          onChange={(e) => onStatusChange(e.target.value)}
          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md border"
        >
          <option value="all">{t('settingsMember.filters.allStatuses')}</option>
          <option value="active">{t('settingsMember.status.active')}</option>
          <option value="inactive">{t('settingsMember.status.inactive')}</option>
        </select>
      </div>
    </div>
  );
};
